import { Controller, useFormContext } from "react-hook-form";
import EditorInput, { EditorInputProps } from "../EditorInput";
import { useEffect, useMemo, useState } from "react";
import useTableDropdown from "../../../table/useTableDropdown";
import axios from "../../../../utils/api/axios";
import useProgram from "../../../../utils/hooks/programs/useProgram";

interface ProgramRun {
    run_id: number;
    run_title: string;
    start_date: string;
    end_date: string;
    status: string;
    run_type: string;
}

const RunDropdown = ({
    value,
    onChange,
    programId,
    architectureVersion
}: {
    value?: number;
    onChange: (value: number) => void;
    programId?: number;
    architectureVersion?: number;
}) => {
    const [runs, setRuns] = useState<ProgramRun[]>([]);
    const [loading, setLoading] = useState(!!programId);
    const [selectedRun, setSelectedRun] = useState<ProgramRun | null>(null);
    const { setValue: setFormValue, register } = useFormContext();

    useEffect(() => {
        register("isRunsLoading");
    }, [register]);

    // Fetch active runs when program changes
    useEffect(() => {
        if (!programId) {
            setRuns([]);
            setFormValue("isRunsLoading", false);
            return;
        }

        const fetchRuns = async () => {
            setLoading(true);
            setFormValue("isRunsLoading", true);
            try {
                const response = await axios.get(`/v2/reports/active-runs/${programId}`);
                setRuns(response.data);

                // Auto-select if only one run exists
                if (response.data.length === 1) {
                    onChange(response.data[0].run_id);
                    setSelectedRun(response.data[0]);
                }
            } catch (error) {
                console.error("Error fetching active runs:", error);
                setRuns([]);
            } finally {
                setLoading(false);
                setFormValue("isRunsLoading", false);
            }
        };

        fetchRuns();
    }, [programId, onChange, setFormValue]);

    // Update selected run when value changes
    useEffect(() => {
        if (value && runs.length > 0) {
            const run = runs.find(r => String(r.run_id) === String(value));
            setSelectedRun(run || null);
        } else if (!value) {
            setSelectedRun(null);
        }
    }, [value, runs]);

    const runOptions = useMemo(
        () =>
            runs.map((run) => ({
                value: run.run_id,
                content: (
                    <div className="flex flex-col gap-1 py-1">
                        <div className="font-medium text-sm">{run.run_title}</div>
                        <div className="text-xs text-gray-500">
                            {new Date(run.start_date).toLocaleDateString()} - {new Date(run.end_date).toLocaleDateString()}
                            <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                                {run.status}
                            </span>
                            <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                {run.run_type}
                            </span>
                        </div>
                    </div>
                )
            })),
        [runs]
    );

    const { dropdown, setValue } = useTableDropdown({
        name: "runs",
        placeholder: loading ? "Loading runs..." : runs.length === 0 ? "No active runs available" : "Select a program run...",
        options: runOptions,
        columns: [
            {
                name: "Program Run"
            }
        ],
        onSearch: () => { }, // No search functionality needed for runs
        onSubmit: (val) => {
            onChange(val);
            const run = runs.find(r => String(r.run_id) === String(val));
            setSelectedRun(run || null);
        }
    });

    useEffect(() => {
        if (selectedRun) {
            setValue(selectedRun.run_title);
        } else if (loading) {
            setValue("Loading run details...");
        } else {
            setValue("");
        }
    }, [selectedRun, loading, setValue]);

    if (!programId) {
        return (
            <div className="text-sm text-gray-500 italic py-2">
                Please select a program first
            </div>
        );
    }

    if (runs.length === 0 && !loading) {
        // If this is a v1 program, don't show the warning as runs are optional/not used
        if (architectureVersion === 1) {
            return (
                <div className="text-sm text-gray-400 italic py-2">
                    No active runs (Optional for legacy programs)
                </div>
            );
        }

        return (
            <div className="text-sm text-orange-600 italic py-2">
                ⚠️ No active runs available for this program. Please contact the program administrator.
            </div>
        );
    }

    return <>{dropdown}</>;
};

const RunSelector = ({
    name,
    inputTitle,
    details,
    required
}: EditorInputProps) => {
    const { control, watch } = useFormContext();
    const programId = watch("programId");
    const { program } = useProgram(programId);

    // Run is only required for v2 programs
    const effectiveRequired = useMemo(() => {
        if (!programId) return required;
        if (!program) return required;
        return required && program.architecture_version !== 1;
    }, [program, programId, required]);

    return (
        <EditorInput
            name={name}
            inputTitle={inputTitle}
            details={details}
            required={effectiveRequired}
        >
            <Controller
                name={name}
                control={control}
                rules={{
                    required: effectiveRequired ? "Please select a program run" : false
                }}
                render={({ field: { value, onChange } }) => (
                    <RunDropdown
                        value={value}
                        onChange={onChange}
                        programId={programId}
                        architectureVersion={program?.architecture_version}
                    />
                )}
            />
        </EditorInput>
    );
};

export default RunSelector;
